import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import dayjs from 'dayjs';

import TicketDetails from '../activeTicket/TicketDetails';
import CustomerDetails from '../activeTicket/CustomerDetails';
import { PubSubKeys, TicketStatuses } from '../../../../store/constants';
import { reissueTicket } from '../../../../store/actions/ticketsActions';
import { modalUpdate } from '../../../../store/actions/settingsActions';

const getServiceParentId = (service = {}) => service.parent || service.parentId || null;

const getServiceHierarchyLabel = (service, services = []) => {
  if (!service) {
    return 'Unavailable';
  }

  const servicesById = services.reduce((acc, currentService) => {
    acc[currentService.id] = currentService;
    return acc;
  }, {});
  const servicePath = [service.name];
  const visitedServiceIds = new Set([service.id]);
  let parentServiceId = getServiceParentId(service);

  while (parentServiceId && servicesById[parentServiceId] && !visitedServiceIds.has(parentServiceId)) {
    const parentService = servicesById[parentServiceId];
    servicePath.unshift(parentService.name);
    visitedServiceIds.add(parentService.id);
    parentServiceId = getServiceParentId(parentService);
  }

  return servicePath.join(' / ');
};

function ReissueTicket(props) {
  const navigate = useNavigate();
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [submittingAction, setSubmittingAction] = useState(null);

  const {
    activeTicketId,
    authenticatedUser,
    branchId,
    customers,
    hostname,
    organizationId,
    services,
    sessions,
    supportDeskId,
    supportDesks,
    tickets,
    users,
  } = props;

  const oldTicket = tickets.find(ticket => ticket.id === activeTicketId);
  const customer = oldTicket ? customers.find(data => data.mobileNumber === oldTicket.mobileNumber) : null;
  const currentService = oldTicket ? services.find(service => service.id === oldTicket.serviceId) : null;
  const supportDesk = supportDesks.find(data => data.id === supportDeskId);
  const onlineAgents = sessions
    .filter(session => Number(session.status) === 1)
    .map(session => users.find(agent => session.userId === agent.id))
    .filter(Boolean);

  const eligibleServices = services
    .filter(service => authenticatedUser.services.includes(service.id))
    .filter(service => oldTicket && service.id !== oldTicket.serviceId)
    .map(service => ({
      ...service,
      hierarchyLabel: getServiceHierarchyLabel(service, services),
      users: onlineAgents.filter(agent => agent.services.includes(service.id)).length
    }))
    .sort((serviceA, serviceB) => serviceA.hierarchyLabel.localeCompare(serviceB.hierarchyLabel));
  const selectedService = eligibleServices.find(service => service.id === selectedServiceId);
  const canProcessSelectedService = selectedService
    ? authenticatedUser.services.includes(selectedService.id)
    : false;

  const getReissuePayload = () => {
    if (!oldTicket || !selectedService) {
      return null;
    }

    const safeAuthenticatedUser = _.omit(authenticatedUser, ['password', 'accessToken', 'refreshToken']);
    return {
      ...PubSubKeys.reissueTicket,
      msg: {
        oldTicket: _.cloneDeep(oldTicket),
        newServiceId: selectedService.id,
        authenticatedUser: safeAuthenticatedUser,
        supportDesk,
        organizationId,
        branchId,
      }
    };
  };

  const getAutoCall = () => {
    const safeAuthenticatedUser = _.omit(authenticatedUser, [
      'accessToken',
      'refreshToken',
      'branchId',
      'email',
      'status',
    ]);

    return {
      enabled: true,
      payload: {
        ...PubSubKeys.callTicket,
        msg: {
          ticket: {
            id: null,
            authenticatedUser: safeAuthenticatedUser,
            sessionStart: dayjs().unix(),
            volume: -1,
            status: TicketStatuses.callTicket,
            callType: 'direct',
            manage: {
              supportDesk: _.cloneDeep(supportDesk),
              userId: authenticatedUser.id,
            },
          }
        }
      }
    };
  };

  const finishReissue = () => {
    props.modalUpdate({ visible: 0, content: null, modalType: 0 });
    navigate('/queuing/tickets/');
  };

  const submitReissue = async (processImmediately = false) => {
    const payload = getReissuePayload();

    if (!payload || submittingAction) {
      return;
    }

    setSubmittingAction(processImmediately ? 'process' : 'queue');
    if (processImmediately) {
      payload.msg.autoCall = getAutoCall();
    }

    await props.reissueTicket(hostname, payload);

    finishReissue();
  };

  if (!oldTicket) {
    return <h5>No active ticket selected</h5>;
  }

  return (
    <div className="home-center-content1 home-center-content3">
      <h5>Re-Assign Ticket</h5>
      <div className="informationContainer">
        <div className="informationRow">
          <label>Current Service</label>
          <span>{getServiceHierarchyLabel(currentService, services)}</span>
        </div>
        {selectedService ? (
          <div className="informationRow">
            <label>New Service</label>
            <span>{getServiceHierarchyLabel(selectedService, services)}</span>
          </div>
        ) : null}
      </div>

      {!selectedService && (
        <div className="reissueServicePicker">
          {!eligibleServices.length && (
            <p>No other subscribed services available</p>
          )}
          {eligibleServices.length ? (
            <select
              aria-label="New service"
              value={selectedServiceId}
              onChange={(event) => setSelectedServiceId(event.target.value)}
            >
              <option value="">Select new service</option>
              {eligibleServices.map(service => (
                <option key={service.id} value={service.id}>
                  {service.hierarchyLabel} ({service.users} Agent{service.users !== 1 ? 's' : ''} online)
                </option>
              ))}
            </select>
          ) : null}
        </div>
      )}

      {selectedService && (
        <>
          <div className="reissueServicePicker">
            <select
              aria-label="New service"
              value={selectedServiceId}
              onChange={(event) => setSelectedServiceId(event.target.value)}
            >
              <option value="">Select new service</option>
              {eligibleServices.map(service => (
                <option key={service.id} value={service.id}>
                  {service.hierarchyLabel} ({service.users} Agent{service.users !== 1 ? 's' : ''} online)
                </option>
              ))}
            </select>
            <p>{selectedService.users} Agent{selectedService.users !== 1 ? 's' : ''} online for this service.</p>
          </div>
          <div className="home-right-content2-bottom">
            <TicketDetails ticket={oldTicket} />
            <CustomerDetails ticket={oldTicket} customer={customer} />
          </div>
          <div className="reissueActions">
            {canProcessSelectedService ? (
              <div
                className={`form-submit-btn reissue-primary ${submittingAction ? 'mute' : ''}`}
                onClick={() => submitReissue(true)}
              >
                {submittingAction === 'process' ? 'Re-assigning...' : 'Re-Assign and Serve'}
              </div>
            ) : null}
            <div
              className={`form-submit-btn ${submittingAction ? 'mute' : ''}`}
              onClick={() => submitReissue(false)}
            >
              {submittingAction === 'queue' ? 'Re-assigning...' : 'Re-Assign Only'}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const mapStateToProps = state => {
  const { auth, customers, services, sessions, tickets, users } = state;
  return {
    hostname: auth.hostname,
    organizationId: auth.organizationId,
    branchId: auth.branchId,
    supportDeskId: auth.supportDeskId,
    authenticatedUser: auth.authenticatedUser,
    activeTicketId: tickets.activeTicketId,
    tickets: tickets.tickets,
    customers: customers.customers,
    services: services.services,
    sessions: sessions.sessions,
    supportDesks: services.supportDesks,
    users: users.users,
  };
};

export default connect(mapStateToProps, {
  modalUpdate,
  reissueTicket,
})(ReissueTicket);

export { getServiceHierarchyLabel };
